import React, { useState, useRef, useEffect } from 'react';

interface Option {
  label: string;
  value: string;
}

interface FilterableMultiSelectProps {
  label: string;
  options: Option[];
  selected: string[]; // this stores the selected GUIDs
  setSelected: (values: string[]) => void;
}

const FilterableMultiSelect: React.FC<FilterableMultiSelectProps> = ({
  label,
  options,
  selected,
  setSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleSelection = (value: string) => {
    if (selected.includes(value)) {
      setSelected(selected.filter(v => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  const filtered = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  const getLabel = (value: string) =>
    options.find(opt => opt.value === value)?.label || value;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative text-[11px]" ref={wrapperRef}>
      <label className="block text-gray-400 mb-1">{label}</label>
      <div
        className="bg-neutral-800 text-white px-3 py-2 rounded cursor-pointer border border-neutral-700"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected.length > 0 ? selected.map(getLabel).join(', ') : 'Select...'}
      </div>
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-neutral-900 border border-neutral-700 rounded shadow-lg max-h-48 overflow-y-auto">
          <input
            type="text"
            placeholder="Search..."
            className="w-full px-3 py-2 bg-neutral-800 text-white text-[11px] border-b border-neutral-700 outline-none"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="max-h-40 overflow-y-auto">
            {filtered.map(option => (
              <div
                key={option.value}
                className={`px-3 py-1.5 cursor-pointer hover:bg-neutral-700 text-[11px] ${
                  selected.includes(option.value)
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-300'
                }`}
                onClick={() => toggleSelection(option.value)}
              >
                {option.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-gray-500 text-[11px]">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterableMultiSelect;
